import React, { SyntheticEvent } from 'react';
import Input from '../../../components/Input';
import BankCodeDropdown from '../../../components/BankCodeDropdown';
import SectionLabel from '../../../components/SectionLabel';
import { Checkbox, Grid } from 'semantic-ui-react';
import { Field } from 'formik';
import { FormikErrorMessage } from '../../../components/ErrorMessage';
import { FieldLabel } from '../../../styles';
import {
  formatPercentageValue,
  isPercentageAboveMaximum,
  maskPercentageValue,
  parsePercentageValue
} from '../../../utils/percentage';
import { dangerNotification } from '../../../services/notification';
import { EstateFormProps } from '../UpdateEstateForm';
import { UserRolesEnum } from '../../../enums/user-roles.enum';
import Button from '../../../components/Button';
import { useHistory } from 'react-router-dom';
import { useStandaloneChargeAccountStatus } from '../../standalone-charges/hooks/useStandaloneChargeAccountStatus';
import EditBankAccountModal from '../../standalone-charges/components/EditBankAccountModal';
import { StandaloneChargeActivationStateEnum } from '../../../services/standalone-charges';
import { getEstateBankByCode } from '../../../resources/bankCodeList';

export const BankAccountData = (props: EstateFormProps) => {
  const {
    errors,
    touched,
    handleInputChange,
    setFieldValue,
    values,
    initialValues
  } = props;
  const canViewBillingConfiguration =
    props.user.role === UserRolesEnum.brokerAdmin ||
    props.user.role === UserRolesEnum.estateAdmin;
  const history = useHistory();
  const chargesEnabledInForm = Boolean(values.standaloneChargesEnabled);
  const persistedChargesEnabled = Boolean(initialValues.standaloneChargesEnabled);
  const showChargesConnection = persistedChargesEnabled && chargesEnabledInForm;
  const needsSaveToEnableConnection = chargesEnabledInForm && !persistedChargesEnabled;
  const {
    connected,
    loading: accountLoading,
    activationStatus,
    refresh: refreshAccountStatus
  } = useStandaloneChargeAccountStatus(values.id, showChargesConnection);
  const [editBankAccountOpen, setEditBankAccountOpen] = React.useState(false);

  const handleCommissionPercentBlur = () => {
    if (isPercentageAboveMaximum(values.standaloneChargesCommissionPercent)) {
      dangerNotification(
        'Percentual Repassado',
        'O percentual repassado não pode ser maior que 100,00.'
      );
      return;
    }

    setFieldValue(
      'standaloneChargesCommissionPercent',
      formatPercentageValue(values.standaloneChargesCommissionPercent)
    );
  };
  const openChargesOnboarding = () => {
    history.push(`/admin/standalone-charges?estateId=${values.id}&tab=estates`);
  };
  const activationState = activationStatus?.activation?.status;
  const hasActivationRecord = Boolean(
    activationState && activationState !== StandaloneChargeActivationStateEnum.notStarted
  );
  const activationLabel =
    activationState === StandaloneChargeActivationStateEnum.pagarmePending
      ? 'Autenticação biométrica pendente.'
      : activationState === StandaloneChargeActivationStateEnum.biometryRejected
      ? 'Biometria recusada.'
      : activationState === StandaloneChargeActivationStateEnum.supportRequired
      ? 'Cadastro aguardando análise do suporte.'
      : activationState === StandaloneChargeActivationStateEnum.pagarmeRejected
      ? 'Cadastro recusado pela Pagar.me.'
      : 'Cadastro ainda não está ativo.';
  const bankAccount = activationStatus?.activation?.defaultBankAccount || {};
  const bankCode = String(bankAccount.bank || '');
  const bankBranchNumber = String(bankAccount.branch_number || '');
  const bankBranchCheckDigit = String(bankAccount.branch_check_digit || '');
  const bankAccountNumber = String(bankAccount.account_number || '');
  const bankAccountCheckDigit = String(bankAccount.account_check_digit || '');
  const [bankLabel, setBankLabel] = React.useState('');

  React.useEffect(() => {
    if (!bankCode) {
      setBankLabel('');
      return;
    }

    let active = true;
    getEstateBankByCode(bankCode)
      .then(bank => active && setBankLabel(bank.text))
      .catch(() => active && setBankLabel(bankCode));

    return () => {
      active = false;
    };
  }, [bankCode]);

  const operationFee = parsePercentageValue(
    values.bankAccount?.standaloneChargesSplitPercent
  );
  const operationFeeLabel =
    operationFee === undefined
      ? '-'
      : `${operationFee.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
  return (
    <>
      {!persistedChargesEnabled && (
        <>
          <SectionLabel text="DADOS BANCÁRIOS" />

          <Grid.Row>
            <Grid.Column width={6}>
              <FieldLabel>Código do Banco*</FieldLabel>
              <Field
                name="bankAccount.bankCode"
                className={
                  errors && errors.bankAccount && touched && touched.bankAccount
                    ? 'text-input error'
                    : 'text-input'
                }
                onChange={(e: SyntheticEvent, data: any) =>
                  handleInputChange(data, props)
                }
                component={BankCodeDropdown}
                maxLength={3}
                fluid
              />
              <FormikErrorMessage component="div" name="bankAccount.bankCode" />
            </Grid.Column>

            <Grid.Column width={4}>
              <FieldLabel>Agência*</FieldLabel>
              <Field
                name="bankAccount.agencyNumber"
                className={
                  errors && errors.bankAccount && touched && touched.bankAccount
                    ? 'text-input error'
                    : 'text-input'
                }
                onChange={(e: SyntheticEvent, data: any) =>
                  handleInputChange(data, props)
                }
                component={Input}
                maxLength={4}
                fluid
              />
              <FormikErrorMessage component="div" name="bankAccount.agencyNumber" />
            </Grid.Column>

            <Grid.Column width={1} style={{ padding: 1 }}>
              <Field
                name="bankAccount.agencyDv"
                style={{ marginTop: '18px' }}
                className={
                  errors && errors.bankAccount && touched && touched.bankAccount
                    ? 'text-input error'
                    : 'text-input'
                }
                onChange={(e: SyntheticEvent, data: any) =>
                  handleInputChange(data, props)
                }
                component={Input}
                maxLength={1}
                fluid
              />
              <FormikErrorMessage component="div" name="bankAccount.agencyDv" />
            </Grid.Column>

            <Grid.Column width={4}>
              <FieldLabel>Conta*</FieldLabel>
              <Field
                name="bankAccount.accountNumber"
                className={
                  errors && errors.bankAccount && touched && touched.bankAccount
                    ? 'text-input error'
                    : 'text-input'
                }
                onChange={(e: SyntheticEvent, data: any) =>
                  handleInputChange(data, props)
                }
                component={Input}
                fluid
              />
              <FormikErrorMessage component="div" name="bankAccount.accountNumber" />
            </Grid.Column>

            <Grid.Column width={1} style={{ padding: 3 }}>
              <Field
                name="bankAccount.accountDv"
                style={{ marginTop: '18px' }}
                className={
                  errors && errors.bankAccount && touched && touched.bankAccount
                    ? 'text-input error'
                    : 'text-input'
                }
                onChange={(e: SyntheticEvent, data: any) =>
                  handleInputChange(data, props)
                }
                component={Input}
                maxLength={1}
                fluid
              />
              <FormikErrorMessage component="div" name="bankAccount.accountDv" />
            </Grid.Column>
          </Grid.Row>
        </>
      )}

      {canViewBillingConfiguration && (
        <>
          <SectionLabel text="INFORMAÇÕES DE COBRANÇA" />
          <Grid.Row>
            <Grid.Column width={16}>
              <p style={{ color: '#666', margin: 0 }}>
                Configure a conta onde deseja receber os valores das cobranças realizadas
                pelo painel.
              </p>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              <FieldLabel style={{ marginBottom: '5px' }}>
                {values.standaloneChargesEnabled
                  ? 'Envio de cobranças habilitado'
                  : 'Envio de cobranças desabilitado'}
              </FieldLabel>
              <Checkbox
                toggle
                checked={Boolean(values.standaloneChargesEnabled)}
                label="Habilitar envio de cobranças"
                onChange={(_, data) => {
                  const enabled = Boolean(data.checked);
                  setFieldValue('standaloneChargesEnabled', enabled);
                  if (!enabled) {
                    setFieldValue('standaloneChargesCommissionEnabled', false);
                  }
                }}
              />
            </Grid.Column>
            {chargesEnabledInForm && (
              <Grid.Column width={4}>
                <FieldLabel style={{ marginBottom: '5px' }}>
                  {values.standaloneChargesCommissionEnabled
                    ? 'Comissionamento habilitado'
                    : 'Comissionamento desabilitado'}
                </FieldLabel>
                <Checkbox
                  toggle
                  label="Habilitar comissionamento"
                  checked={Boolean(values.standaloneChargesCommissionEnabled)}
                  onChange={(_, data) =>
                    setFieldValue(
                      'standaloneChargesCommissionEnabled',
                      Boolean(data.checked)
                    )
                  }
                />
              </Grid.Column>
            )}
          </Grid.Row>

          {chargesEnabledInForm && (
            <>
              {needsSaveToEnableConnection && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    <p style={{ color: '#666', margin: '0 0 12px', fontSize: '0.9em' }}>
                      Salve o cadastro após habilitar cobranças para conectar a conta na
                      Pagar.me.
                    </p>
                  </Grid.Column>
                </Grid.Row>
              )}

              <Grid.Row>
                <Grid.Column width={8}>
                  <FieldLabel>
                    Conta bancária para Cobranças -{' '}
                    {accountLoading
                      ? 'Consultando'
                      : connected
                      ? 'Conectada'
                      : hasActivationRecord
                      ? activationLabel.replace(/\.$/, '')
                      : 'Desconectada'}
                  </FieldLabel>
                  <div
                    style={{
                      background: '#f1f1f1',
                      padding: 10,
                      minHeight: 74,
                      borderRadius: 3
                    }}
                  >
                    {connected ? (
                      <>
                        <Grid stackable style={{ margin: 0 }}>
                          <Grid.Column width={8} style={{ padding: '0 8px 8px 0' }}>
                            <div style={{ fontSize: '0.85em', marginBottom: 6 }}>
                              Banco
                            </div>
                            <div style={{ color: '#666', fontSize: '0.85em' }}>
                              {bankLabel || bankCode || '-'}
                            </div>
                          </Grid.Column>
                          <Grid.Column width={4} style={{ padding: '0 8px 8px 0' }}>
                            <div style={{ fontSize: '0.85em', marginBottom: 6 }}>
                              Agência
                            </div>
                            <div style={{ color: '#666', fontSize: '0.85em' }}>
                              {bankBranchNumber}
                              {bankBranchCheckDigit ? `-${bankBranchCheckDigit}` : ''}
                            </div>
                          </Grid.Column>
                          <Grid.Column width={4} style={{ padding: '0 0 8px 0' }}>
                            <div style={{ fontSize: '0.85em', marginBottom: 6 }}>
                              Conta
                            </div>
                            <div style={{ color: '#666', fontSize: '0.85em' }}>
                              {bankAccountNumber}
                              {bankAccountCheckDigit ? `-${bankAccountCheckDigit}` : ''}
                            </div>
                          </Grid.Column>
                        </Grid>
                        <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                          <Button
                            type="button"
                            text="Editar cadastro"
                            onClick={() => setEditBankAccountOpen(true)}
                          />
                          <Button
                            type="button"
                            color="red"
                            text="Desconectar"
                            disabled
                            title="A desconexão ainda não está disponível."
                          />
                        </div>
                      </>
                    ) : (
                      <div style={{ color: '#777', fontSize: '0.85em' }}>
                        {accountLoading
                          ? 'Consultando conta...'
                          : hasActivationRecord
                          ? activationLabel
                          : 'Nenhuma conta atribuída.'}
                        <div style={{ marginTop: 8 }}>
                          <Button
                            type="button"
                            text={hasActivationRecord ? 'Acessar cobranças' : 'Conectar'}
                            onClick={openChargesOnboarding}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </Grid.Column>

                <Grid.Column width={8}>
                  <FieldLabel>Taxa de operação</FieldLabel>
                  <div style={{ color: '#333', fontSize: '0.9em' }}>
                    {operationFeeLabel}
                  </div>
                  <p style={{ margin: '6px 0 0', fontSize: '0.85em', color: '#888' }}>
                    Percentual retido pela plataforma sobre o valor de cada cobrança
                    realizada.*
                  </p>
                </Grid.Column>
              </Grid.Row>

              {values.standaloneChargesCommissionEnabled && (
                <Grid.Row>
                  <Grid.Column width={8}>
                    <FieldLabel>Percentual repassado aos colaboradores (%)</FieldLabel>
                    <Field
                      name="standaloneChargesCommissionPercent"
                      onChange={(_: SyntheticEvent, data: any) =>
                        setFieldValue(
                          'standaloneChargesCommissionPercent',
                          maskPercentageValue(data.value)
                        )
                      }
                      onBlur={handleCommissionPercentBlur}
                      component={Input}
                      inputMode="decimal"
                      label="%"
                      labelPosition="right"
                      style={{ maxWidth: 180 }}
                    />
                    <p style={{ margin: '6px 0 0', fontSize: '0.85em', color: '#888' }}>
                      *O percentual comissionado será aplicado para todos os colaboradores
                      válidos. Em caso de mais múltiplos colaboradores, o valor percentual
                      será distribuído igualmente entre as partes comissionadas.
                    </p>
                  </Grid.Column>
                </Grid.Row>
              )}
            </>
          )}
        </>
      )}

      <EditBankAccountModal
        open={editBankAccountOpen}
        mode="estate"
        estateId={values.id}
        source={
          activationStatus?.activation
            ? {
                recipientType: activationStatus.activation.recipientType,
                registerInformation: activationStatus.activation.registerInformation,
                defaultBankAccount: activationStatus.activation.defaultBankAccount,
                payload: activationStatus.activation.payload
              }
            : null
        }
        onClose={() => setEditBankAccountOpen(false)}
        onSuccess={refreshAccountStatus}
      />
    </>
  );
};
